import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {NavBar} from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";

const AIFMDLanding20 = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);

    try {
      // Simulate fetching data
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            ...mockComplianceAiResponse,
            title: "AIFMD Marketing Compliance Assessment",
            summary: "Your financial marketing materials have been analyzed for AIFMD compliance. Here are our recommendations to improve your marketing compliance posture."
          });
        }, 2000);
      });
      
      setResponse(result);
    } catch (error) {
      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>AIFMD v2.0 Compliance Platform | Verdea AI</title>
        <meta 
          name="description" 
          content="AI-powered compliance review for AIFMD marketing materials. Get instant, accurate compliance feedback for your financial marketing content." 
        />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-gray-50">
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar - landing mode hides platform, tools and login buttons */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <NavBar landingMode={true} />
      </header>
      
      <main className="relative z-10 pt-20">
        {/* Hero section */}
        <section className="py-24 bg-transparent">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="lg:w-1/2">
                <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                  Your compliance team, powered by AI
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl">
                  Verdea AI automates AIFMD compliance reviews to reduce risk, save time, and scale your marketing efforts.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                    onClick={() => {
                      const formElement = document.querySelector('#compliance-form-section');
                      if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Try It Free
                  </Button>
                  <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 rounded-full px-8">
                    Book a Demo
                  </Button>
                </div>
                
                <div className="mt-12">
                  <p className="text-sm text-gray-500 mb-4">Financial technology ecosystem</p>
                  <div className="flex flex-wrap gap-8 items-center">
                    <img src="/logos/ant.png" alt="Industry Organization" className="h-6 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                    <img src="/logos/ejaso.png" alt="Industry Organization" className="h-6 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                    <img src="/logos/mm1.png" alt="Industry Organization" className="h-6 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                    <img src="/logos/vba.png" alt="Industry Organization" className="h-6 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all" />
                  </div>
                </div>
              </div>
              
              <div className="lg:w-1/2">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  <img 
                    src="/screens/aifmd/aifguard_1.png"
                    alt="Verdea AI Dashboard" 
                    className="w-full h-auto object-cover"
                    style={{ maxHeight: "500px" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Features section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">Supercharge your compliance workflow</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Verdea AI helps alternative investment firms meet AIFMD marketing compliance requirements with unprecedented speed and accuracy.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-10">
              {[
                {
                  title: "Automate compliance reviews",
                  description: "Our AI analyzes your marketing materials in seconds, checking against all AIFMD requirements and flagging potential issues.",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )
                },
                {
                  title: "Reduce compliance risks",
                  description: "Our platform continuously monitors your marketing content across all channels, ensuring ongoing compliance with AIFMD regulations.",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )
                },
                {
                  title: "Scale your marketing",
                  description: "Verdea AI reduces review time by up to 90%, allowing your marketing team to create and publish compliant content faster than ever.",
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  )
                }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-8 rounded-2xl"
                >
                  {feature.icon}
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Benefits section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="mb-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
                  <div>
                    <h2 className="text-4xl font-bold mb-4">Why financial firms choose Verdea AI</h2>
                    <p className="text-xl text-gray-600 max-w-2xl">
                      Our AI-powered platform delivers measurable benefits for alternative investment firms marketing under AIFMD regulations.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {[
                    {
                      title: "95% faster reviews",
                      description: "Our AI analyzes marketing materials in seconds instead of days, dramatically accelerating your compliance workflow."
                    },
                    {
                      title: "90% accuracy",
                      description: "Trained on the latest AIFMD regulations and guidelines, our AI delivers human-level accuracy with machine speed."
                    },
                    {
                      title: "Cross-border expertise",
                      description: "Navigate the complexities of marketing funds across different EU jurisdictions with country-specific compliance checks."
                    },
                    {
                      title: "Continuous monitoring",
                      description: "Our platform automatically scans your websites, emails, and social media to ensure ongoing AIFMD compliance."
                    },
                    {
                      title: "Audit-ready reports",
                      description: "Generate comprehensive compliance documentation with one click, making regulatory inspections stress-free."
                    },
                    {
                      title: "Seamless integration",
                      description: "Verdea AI integrates with your existing marketing tools and workflows with minimal disruption."
                    }
                  ].map((benefit, index) => (
                    <div
                      key={index}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* How it works section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">How Verdea AI works</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Our platform uses advanced AI to transform your compliance workflow from start to finish.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                  <img 
                    src="/screens/aifmd/aifguard_2.png"
                    alt="Verdea AI Platform" 
                    className="rounded-2xl shadow-lg w-full h-auto"
                    loading="lazy"
                    style={{ maxHeight: "500px", objectFit: "cover" }}
                  />
                </div>
                
                <div>
                  <ol className="space-y-8">
                    {[
                      {
                        title: "Upload or connect",
                        description: "Easily upload marketing content or connect your marketing channels to Verdea AI."
                      },
                      {
                        title: "AI analysis",
                        description: "Our AI scans your content against all relevant AIFMD regulations and guidelines."
                      },
                      {
                        title: "Review insights",
                        description: "Get detailed compliance insights and specific recommendations to fix any issues."
                      },
                      {
                        title: "Make corrections",
                        description: "Implement the suggested changes or use our AI to automatically fix compliance issues."
                      },
                      {
                        title: "Export and publish",
                        description: "Export compliance documentation and publish your fully compliant marketing content."
                      }
                    ].map((step, index) => (
                      <li key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-1">{step.title}</h3>
                          <p className="text-gray-600">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Stats section */}
        <section className="py-20 bg-emerald-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">The measurable impact of Verdea AI</h2>
                <p className="text-xl opacity-90 max-w-3xl mx-auto">
                  Our customers see significant improvements in compliance efficiency, risk reduction, and marketing output.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  {
                    value: "90%",
                    label: "Reduction in compliance review time"
                  },
                  {
                    value: "75%",
                    label: "Decrease in compliance-related delays"
                  },
                  {
                    value: "3x",
                    label: "Increase in marketing content output"
                  },
                  {
                    value: "99%",
                    label: "Compliance accuracy rate"
                  }
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="bg-emerald-700/30 p-8 rounded-2xl text-center"
                  >
                    <div className="text-5xl font-bold mb-2">{stat.value}</div>
                    <p className="text-lg opacity-90">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Main content with form section */}
        <section id="compliance-form-section" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-4xl font-bold mb-6">Experience Verdea AI in action</h2>
                  <p className="text-xl text-gray-600 mb-8">
                    Try our AIFMD compliance analyzer with your own marketing content to see how our AI can transform your compliance workflow.
                  </p>
                  <ul className="space-y-4 mb-8">
                    {[
                      "Instant AIFMD compliance analysis",
                      "Detailed recommendations and insights",
                      "Actionable fixes for compliance issues",
                      "No registration required"
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-500">
                    For a full demo of our enterprise platform, book a call with our team.
                  </p>
                </div>
                
                <div>
                  <div className="bg-white p-8 rounded-2xl shadow-lg">
                    {!response ? (
                      <ComplianceAIForm 
                        onSubmit={handleFormSubmit} 
                        isLoading={isLoading}
                        error={error}
                      />
                    ) : (
                      <ComplianceAIResponse 
                        response={response} 
                        isLoading={isLoading} 
                        error={error}
                        onReset={() => {
                          setFormData(null);
                          setResponse(null);
                          setError(null);
                        }} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">Frequently asked questions</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Everything you need to know about our AIFMD compliance platform
                </p>
              </div>
              
              <div className="space-y-6">
                {[
                  {
                    question: "How accurate is Verdea AI's compliance analysis?",
                    answer: "Verdea AI achieves a 90% accuracy rate for AIFMD compliance analysis, comparable to expert human reviewers but delivered in a fraction of the time. Our AI is continuously trained on the latest AIFMD regulations and guidelines to ensure the highest level of accuracy."
                  },
                  {
                    question: "Does Verdea AI replace human compliance officers?",
                    answer: "No, Verdea AI is designed to augment and empower human compliance teams, not replace them. Our platform handles routine compliance checks and analysis, freeing up your compliance officers to focus on strategic decision-making and complex compliance issues."
                  },
                  {
                    question: "How quickly can we implement Verdea AI?",
                    answer: "Most customers can implement Verdea AI within days, not weeks or months. Our platform is designed for easy integration with your existing marketing and compliance workflows, and our customer success team provides comprehensive onboarding support."
                  },
                  {
                    question: "How does Verdea AI handle cross-border marketing compliance?",
                    answer: "Verdea AI is trained on the specific AIFMD implementation rules for each EU member state, allowing it to provide jurisdiction-specific compliance guidance for cross-border marketing activities. The platform automatically adapts its analysis based on the target countries you specify."
                  },
                  {
                    question: "Can Verdea AI integrate with our existing marketing tools?",
                    answer: "Yes, Verdea AI offers API integrations with popular marketing platforms, content management systems, and communication tools. Our platform can also monitor your websites, social media accounts, and other digital channels through direct connections."
                  }
                ].map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-6 rounded-xl"
                  >
                    <h3 className="text-xl font-bold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA section */}
        <section className="py-20 bg-emerald-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center">
                <h2 className="text-4xl font-bold mb-6">Ready to transform your AIFMD compliance workflow?</h2>
                <p className="text-xl opacity-90 mb-10 max-w-3xl mx-auto">
                  Join forward-thinking alternative investment firms using Verdea AI to reduce compliance risk, increase marketing output, and scale faster.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-white text-emerald-600 hover:bg-gray-100 rounded-full px-8"
                    onClick={() => {
                      const formElement = document.querySelector('#compliance-form-section');
                      if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Try It Free
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-700 rounded-full px-8">
                    Book a Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default AIFMDLanding20;